"use client";

import React from "react";
import { PenTool, Zap, Settings2, ArrowRight, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const timelineSteps = [{
  title: "Custom AV Design",
  description: "Tailored technology solutions engineered specifically for your unique business environment.",
  icon: PenTool
}, {
  title: "Infrastructure & Electrical",
  description: "Comprehensive low voltage, data, and electrical services to build a robust foundation for advanced technology.",
  icon: Zap
}, {
  title: "Integration & Installation",
  description: "Seamless turnkey deployment by expert technicians, ensuring flawless operation from day one.",
  icon: Settings2
}];
const benefits = ["Discover – We take the time to understand your specific needs.", "Design – We create the best fitting solution for your organization.", "Deliver – We bring that solution to life and provide ongoing support to ensure long-term success."];
export default function TheTurnkeyAdvantage() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="the-turnkey-advantage" className="dark bg-background text-foreground relative py-24 lg:py-32 overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute inset-0 bg-muted/30 pointer-events-none" />
      
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-[0.03] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {}
          <div ref={imageRef} className={`relative transition-all duration-1000 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            {}
            <div className="absolute -inset-4 bg-primary/10 blur-2xl rounded-full opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent opacity-20 rounded-2xl" />
            
            <div className="relative rounded-2xl overflow-hidden border border-border/50 bg-card/50 backdrop-blur-sm shadow-2xl shadow-black/50 group">
              
              <img src="https://suitable-martin.10web.cloud/wp-content/uploads/2026/03/NRBVW_010-scaled.jpg" alt="Clean and organized AV rack wiring demonstrating precision integration" className="w-full h-auto object-cover aspect-[4/5] lg:aspect-square transition-transform duration-700 group-hover:scale-105 grayscale-[20%] contrast-125" />
              
              {}
              <div className="absolute bottom-6 left-6 right-6 z-20 bg-background/80 backdrop-blur-md border border-border/50 rounded-xl p-4 flex items-center gap-4 shadow-lg">
                <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0 border border-primary/30 !text-[hsl(0,95%,41%)]">
                  <CircleCheck className="size-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Workplace Technology Solutions</p>
                  <p className="text-xs text-muted-foreground">Precision Engineering</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={contentRef} className={`flex flex-col justify-center transition-all duration-1000 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-sm font-medium w-fit mb-6 !text-[hsl(0,95%,41%)]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              Concept to Completion
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-foreground">
              The <span className="!text-[hsl(0,95%,41%)]">Turnkey</span> Advantage
            </h2>
            
            <p className="text-lg text-muted-foreground mb-10 leading-relaxed">As a premier turnkey provider, WTS is your one-stop shop for comprehensive AV solutions. We specialize in delivering fully integrated, custom audiovisual systems tailored to your unique business environment, managing every detail from initial design to final deployment.</p>

            {}
            <div className="relative border-l border-border/60 ml-4 md:ml-6 pl-8 md:pl-10 space-y-10 mb-12">
              {timelineSteps.map((step, i) => <div key={i} data-index={i} className="relative group">
                  {}
                  <div className="absolute -left-[49px] md:-left-[57px] top-0.5 h-10 w-10 rounded-full bg-background border-2 border-border group-hover:border-primary transition-colors duration-300 flex items-center justify-center shadow-sm z-10">
                    <step.icon className="size-4 text-muted-foreground group-hover:text-primary transition-colors duration-300" />
                  </div>
                  
                  {}
                  <div className="bg-card/30 hover:bg-card/50 transition-colors duration-300 border border-border/50 rounded-xl p-5 backdrop-blur-sm">
                    <h3 className="text-xl font-semibold text-foreground mb-2 flex items-center gap-2">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>)}
            </div>

            {}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between pt-6 border-t border-border/50">
              <ul className="space-y-2">
                {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CircleCheck className="size-4 text-primary shrink-0" />
                    <span>{benefit}</span>
                  </li>)}
              </ul>
              
              
            </div>

          </div>
        </div>
      </div>
    </section>;
}